import { t } from "i18next";
import { FC, useContext } from "react";
import { Container, Row } from "react-bootstrap";
import { toast } from "react-toastify";

import Icons from "@app/assets/Icons";
import LoadingButton from "@app/components/Button";
import BootstrapModal from "@app/components/Modal";
import TextInput from "@app/components/inputs/Text";
import { UserPermissions } from "@app/types/Common.types";
import { DownloadLinkData } from "@app/types/VersionControl";
import { AppContext } from "@app/utils/contexts/App.context";

import "./index.scss";

interface DownloadLinkModalProps {
    showModal: boolean;
    setShowModal: (_show: boolean) => void;
    handleGenerateDownloadLink: () => void;
    downloadLinkData?: DownloadLinkData;
}

const DownloadLinkModal: FC<DownloadLinkModalProps> = ({
    showModal,
    setShowModal,
    handleGenerateDownloadLink,
    downloadLinkData,
}) => {
    const { userPermissions } = useContext(AppContext);
    const handleCopy = () => {
        navigator.clipboard.writeText(String(downloadLinkData?.downloadUrl));
        toast.success(t("VERSION_MANAGEMENT.DOWNLOAD.COPY_MESSAGE"));
    };

    const isGenerateDisabled = (
        isAlbaikAdmin?: boolean,
        expiryDate?: string,
        userPermissions?: UserPermissions,
    ): boolean => {
        if (isAlbaikAdmin)
            return false;
        const hasPermission =
            userPermissions &&
            (userPermissions["Create_Deployment Automation"] ||
                userPermissions["Edit_Deployment Automation"]);
        if (!hasPermission) return true;
        if (expiryDate) {
            if (!isNaN(new Date(expiryDate).getTime()) && new Date(expiryDate) > new Date()) {
                return true;
            }
        }
        return false;
    };

    const generateDownloadLinkOnClick = () => {
        handleGenerateDownloadLink();
        toast.success(t("VERSION_MANAGEMENT.DOWNLOAD.GENERATE_LINK_MESSAGE"));
    };

    return (
        <div>
            <BootstrapModal
                header={t("MODAL.DOWNLOAD_LINK_MODAL.HEADER")}
                open={showModal}
                onClose={() => setShowModal(false)}
            >
                <Container>
                    <Row className='mt-4 download-link__inputField'>
                        <TextInput
                            type='text'
                            label={t("MODAL.DOWNLOAD_LINK_MODAL.BODY") as string}
                            placeholder='XXXXXXXXXXXXXXXX'
                            value={
                                downloadLinkData?.expiryDate
                                    ? (downloadLinkData?.downloadUrl as string)
                                    : ""
                            }
                            disabled={false}
                        />
                        <img
                            className={`download-link__copyIcon ${!downloadLinkData ? "disabled" : ""
                                }`}
                            alt='copy-icon'
                            src={Icons.copyIcon}
                            onClick={downloadLinkData ? handleCopy : undefined}
                        />
                        <div>
                            <span className='download-link__valid-message'>
                                {downloadLinkData?.validUntilMessage}
                            </span>
                        </div>
                    </Row>
                    <Row
                        className={`download-link__generateBtnContainer ${isGenerateDisabled(downloadLinkData?.isAlbaikAdmin, downloadLinkData?.expiryDate) ? "disabled-btn" : ""
                            }`}
                    >
                        <LoadingButton
                            btnClass={`download-link__generateButton ${isGenerateDisabled(downloadLinkData?.isAlbaikAdmin, downloadLinkData?.expiryDate, userPermissions)
                                ? "disabled-btn"
                                : ""
                                }`}
                            variant='add'
                            label={t("MODAL.DOWNLOAD_LINK_MODAL.GENERATE_LINK")}
                            onClick={() => {
                                generateDownloadLinkOnClick();
                            }}
                            disabled={isGenerateDisabled(
                                downloadLinkData?.isAlbaikAdmin,
                                downloadLinkData?.expiryDate,
                                userPermissions,
                            )}
                        />
                    </Row>
                </Container>
            </BootstrapModal>
        </div>
    );
};

export default DownloadLinkModal;
